<template>
  <div class="cms-input w-100" :invalid="invalid" :styled="styled">
    <div class="text-left label-container">
      <label>{{fullName}}</label>
    </div>
    <div class="input-container" style="position:relative;">
      <div>
        <input
          :class="['form-control', {'w-90': eyeShowed}]"
          :type="inputType"
          :placeholder="placeholder"
          :value="value"
          :disabled="disabled"
          :maxlength="maxlength"
          @input="$emit('input', $event.target.value)"
          @focusout="onFocusout"
        />
        <div class="w-10 d-inline-block" v-if="eyeShowed" @click="showPassword = !showPassword">
          <cms-icon width="20" height="20">
            <eye-blocked v-if="showPassword" />
            <eye-open v-else />
          </cms-icon>
        </div>
      </div>
      <cms-loader class="loader" v-if="isRemoteValidationPending"></cms-loader>
    </div>
  </div>
</template>

<script>
import eyeOpen from "../icons/eye-open";
import eyeBlocked from "../icons/eye-blocked";
import validationRules from "../../mixins/validationRules";
import { mapMutations } from "vuex";

export default {
  name: "cms-input",
  mixins: [validationRules],
  props: {
    name: String,
    placeholder: String,
    required: Boolean,
    disabled: Boolean,
    value: String,
    maxlength: Number,
    email: Boolean,
    phone: Boolean,
    remoteValidation: String,
    validatePassword: Boolean,
    eyeToggle: Boolean,
    type: {
      type: String,
      default: "text",
    },
    styled: {
      type: String,
      default: "multiline",
    },
  },
  components: {
    "eye-open": eyeOpen,
    "eye-blocked": eyeBlocked,
  },
  data() {
    return {
      isRemoteValidationPending: false,
      remoteValidationMessage: null,
      _validationVisible: false,
      showPassword: false
    };
  },
  computed: {
    fullName() {
      return this.name + ":" + (this.required ? "*" : "");
    },
    invalid() {
      return this.$data._validationVisible && !this.$valid;
    },
    remoteValid() {
      return !(
        this.remoteValidation &&
        (this.$data.isRemoteValidationPending ||
          this.$data.remoteValidationMessage)
      );
    },
    $valid() {
      return !this.validationMessage && this.remoteValid;
    },
    validationMessage() {
      if (this.required && this.isEmpty()) {
        return `The ${this.name} field is required.`;
      }
      if (this.maxlength && this.exceedsMaxLength()) {
        return `The ${this.name} length should not exceed ${this.maxlength} characters.`;
      }
      if (this.email && !this.isEmail()) {
        return `The ${this.name} is invalid.`;
      }
      if (this.phone && !this.numericOnly()) {
        return `The ${this.name} should be numeric only.`;
      }
      if (this.validatePassword && !this.isCmsPassword()) {
        return `The ${this.name} must be 8-20 characters long with at least 1 upper case letter, 1 lower case letter and 1 number.`;
      }
    },
    eyeShowed() {
      return (
        this.type === "password" &&
        this.eyeToggle === true &&
        this.value &&
        this.value.length
      );
    },
    inputType() {
      if (this.type === "password" && this.showPassword) {
        return "text";
      }
      return this.type;
    },
  },
  methods: {
    ...mapMutations(["addValidationError"]),
    async onFocusout() {
      this.$showValidation();
      if (
        this.remoteValidation &&
        !this.validationMessage &&
        !this.isRemoteValidationPending
      ) {
        this.isRemoteValidationPending = true;
        this.$emit("remoteValidationStarts", this.name);

        let message = await this.$store.dispatch(
          this.remoteValidation,
          this.value
        );
        this.$data.remoteValidationMessage = message;
        message && this.addValidationError({name: this.name, message: message});

        this.$emit("remoteValidationEnds", {
          name: this.name,
          success: !message,
        });
        this.isRemoteValidationPending = false;
      }
    },
    $showValidation() {
      this.$data._validationVisible = true;
      this.validationMessage && this.addValidationError({name: this.name, message: this.validationMessage});
    }
  }
};
</script>

<style scoped lang="scss">
.cms-input[invalid="true"] {
  label {
    color: $coral-color;
  }
}

.cms-input[styled="multiline"] {
  .label-container {
    width: 100%;
  }

  .input-container {
    width: 100%;
  }
}

.cms-input[styled="inline"] {
  display: flex;
  justify-content: center;

  .label-container {
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .input-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 50%;
  }
}

.form-control {
  border-width: 0;
  border-radius: 0;
  border-color: #d1d1d1;
  background-color: white;
  letter-spacing: -0.5px;
  color: #161719;
  box-shadow: none;
}

label {
  color: $grey-one-color;
  display: inline-block;
  vertical-align: middle;
  line-height: normal;
  margin: 0;
}

input {
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  display: inline-block;
  &[disabled="disabled"] {
    color: $grey-one-color;
  }
}

input::placeholder {
  font-weight: 350;
  color: $grey-five50-color;
}

.loader {
  float: right;
  transform: scale(0.3);
  transform-origin: top right;
  position: absolute;
  top: 0;
  right: 0;
}
</style>
